import type { MutableRefObject } from 'react';
import { useCallback } from 'react';

type ListRef = MutableRefObject<Map<string, HTMLDivElement>>;

export function useFeatureAnimations(listRef: ListRef) {
	const capturePositions = useCallback((): Map<string, DOMRect> => {
		const pos = new Map<string, DOMRect>();
		listRef.current.forEach((el, id) => {
			if (el) pos.set(id, el.getBoundingClientRect());
		});
		return pos;
	}, [listRef]);

	const animateEntrance = useCallback(
		(enterId: string) => {
			requestAnimationFrame(() => {
				const el = listRef.current.get(enterId);
				if (!el) return;
				el.animate(
					[
						{
							opacity: 0,
							transform:
								'translateY(-24px) scaleY(0.6) scaleX(0.98)',
							offset: 0,
						},
						{
							opacity: 0.6,
							transform:
								'translateY(6px) scaleY(1.03) scaleX(1.005)',
							offset: 0.45,
						},
						{
							opacity: 0.9,
							transform: 'translateY(-3px) scaleY(0.99)',
							offset: 0.7,
						},
						{
							opacity: 1,
							transform: 'translateY(1px) scaleY(1.005)',
							offset: 0.85,
						},
						{
							opacity: 1,
							transform: 'translateY(0) scaleY(1) scaleX(1)',
							offset: 1,
						},
					],
					{
						duration: 700,
						easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
						fill: 'forwards',
					},
				);
			});
		},
		[listRef],
	);

	const animateMove = useCallback(
		(
			oldPositions: Map<string, DOMRect>,
			votedId: string,
			direction: 'up' | 'down',
		) => {
			requestAnimationFrame(() => {
				listRef.current.forEach((el, id) => {
					if (!el) return;
					const oldRect = oldPositions.get(id);
					const newRect = el.getBoundingClientRect();
					if (!oldRect) return;
					const deltaY = oldRect.top - newRect.top;
					if (Math.abs(deltaY) < 1) return;

					const isMain = id === votedId;
					const isUp = direction === 'up';

					if (isMain) {
						el.animate(
							[
								{
									transform: `translateY(${deltaY}px) scaleY(1) scaleX(1)`,
									offset: 0,
								},
								{
									transform: `translateY(${deltaY + (isUp ? 12 : -8)}px) scaleY(0.97) scaleX(1.01)`,
									offset: 0.1,
								},
								{
									transform: `translateY(${deltaY * 0.4}px) scaleY(1.03) scaleX(0.99)`,
									offset: 0.4,
								},
								{
									transform: `translateY(${isUp ? -8 : 6}px) scaleY(1.01) scaleX(1)`,
									offset: 0.65,
								},
								{
									transform: `translateY(${isUp ? 3 : -2}px) scaleY(0.995) scaleX(1.002)`,
									offset: 0.82,
								},
								{
									transform:
										'translateY(0) scaleY(1) scaleX(1)',
									offset: 1,
								},
							],
							{
								duration: 750,
								easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
								fill: 'none',
							},
						);
					} else {
						el.animate(
							[
								{
									transform: `translateY(${deltaY}px)`,
									offset: 0,
								},
								{
									transform: `translateY(${deltaY * 0.5}px)`,
									offset: 0.3,
								},
								{ transform: 'translateY(-3px)', offset: 0.75 },
								{ transform: 'translateY(1px)', offset: 0.9 },
								{ transform: 'translateY(0)', offset: 1 },
							],
							{
								duration: 650,
								easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
								fill: 'none',
								delay: 60,
							},
						);
					}
				});
			});
		},
		[listRef],
	);

	/** Animate all cards reordering (sort change). */
	const animateSort = useCallback(
		(oldPositions: Map<string, DOMRect>) => {
			requestAnimationFrame(() => {
				listRef.current.forEach((el, id) => {
					if (!el) return;
					const oldRect = oldPositions.get(id);
					if (!oldRect) return;
					const newRect = el.getBoundingClientRect();
					const deltaY = oldRect.top - newRect.top;
					if (Math.abs(deltaY) < 1) return;
					el.animate(
						[
							{ transform: `translateY(${deltaY}px)`, offset: 0 },
							{
								transform: `translateY(${deltaY * 0.4}px)`,
								offset: 0.35,
							},
							{ transform: 'translateY(-3px)', offset: 0.78 },
							{ transform: 'translateY(1px)', offset: 0.9 },
							{ transform: 'translateY(0)', offset: 1 },
						],
						{
							duration: 550,
							easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
							fill: 'none',
						},
					);
				});
			});
		},
		[listRef],
	);

	/** Animate existing cards shifting down when a new card is inserted (excludes the new card). */
	const animateShift = useCallback(
		(oldPositions: Map<string, DOMRect>, excludeId: string) => {
			requestAnimationFrame(() => {
				listRef.current.forEach((el, id) => {
					if (!el || id === excludeId) return;
					const oldRect = oldPositions.get(id);
					if (!oldRect) return;
					const newRect = el.getBoundingClientRect();
					const deltaY = oldRect.top - newRect.top;
					if (Math.abs(deltaY) < 1) return;
					el.animate(
						[
							{ transform: `translateY(${deltaY}px)`, offset: 0 },
							{
								transform: `translateY(${deltaY * 0.45}px)`,
								offset: 0.3,
							},
							{ transform: 'translateY(-3px)', offset: 0.75 },
							{ transform: 'translateY(1px)', offset: 0.9 },
							{ transform: 'translateY(0)', offset: 1 },
						],
						{
							duration: 600,
							easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
							fill: 'none',
							delay: 80,
						},
					);
				});
			});
		},
		[listRef],
	);

	return {
		capturePositions,
		animateEntrance,
		animateMove,
		animateSort,
		animateShift,
	};
}
